import type { CSSProperties } from 'react';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  HeadlineL,
  HeadlineM,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import type { MonetaryAmountParams } from '@sorare/core/src/lib/monetaryAmount';

import { useConversionCreditForPrice } from '#src/hooks/useConversionCreditForPrice.ts';

const Line = styled(Horizontal)`
  align-items: baseline;
  flex-wrap: wrap;
  &.crossed {
    text-decoration: line-through;
    color: var(--c-nd-600);
  }
`;

const NoWrapLine = styled(Line)`
  flex-wrap: nowrap;
`;

const CrossedLabelM = styled(LabelM)`
  text-decoration: line-through;
`;

const CrossedLabelL = styled(LabelL)`
  text-decoration: line-through;
`;

type Props = {
  price: MonetaryAmountParams;
  canDiscount?: boolean;
  sport: Sport;
  variant?: 'large' | 'default';
};

export const DiscountedPrice = ({
  price,
  canDiscount,
  sport,
  variant = 'default',
}: Props) => {
  const { canUseConversionCredit, priceDiscountedAmount } =
    useConversionCreditForPrice({ price, sport, canDiscount });

  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: price,
  });
  const { main: discountedMain, exponent: discountedExponent } =
    useAmountWithConversion({
      monetaryAmount: priceDiscountedAmount,
    });

  const { Headline, Label, CrossedLabel } = (() => {
    switch (variant) {
      case 'large':
        return {
          Headline: HeadlineL,
          Label: LabelL,
          CrossedLabel: CrossedLabelL,
        };
      default:
        return {
          Headline: HeadlineM,
          Label: LabelM,
          CrossedLabel: CrossedLabelM,
        };
    }
  })();

  if (canDiscount && canUseConversionCredit) {
    return (
      <>
        <NoWrapLine>
          <CrossedLabel bold color="var(--c-nd-600)" role="deletion">
            {main}
          </CrossedLabel>
          {variant === 'large' && (
            <CrossedLabelM color="var(--c-nd-600)">{exponent}</CrossedLabelM>
          )}
        </NoWrapLine>

        <NoWrapLine>
          <Headline
            className="text-gradient-color"
            style={
              {
                '--text-gradient-color': 'var(--c-gradient-discount)',
              } as CSSProperties
            }
          >
            {discountedMain}
          </Headline>
          {!!exponent && (
            <Label color="var(--c-nd-600)">{discountedExponent}</Label>
          )}
        </NoWrapLine>
      </>
    );
  }
  return (
    <Line>
      <Headline>{main}</Headline>
      {!!exponent && <Label color="var(--c-nd-600)">{exponent}</Label>}
    </Line>
  );
};
